import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface CaseStudy {
  id: number;
  service: string;
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: string[];
  image: string;
  alt: string;
  metrics: {
    label: string;
    value: string;
  }[];
}

const CaseStudyShowcase = () => {
  const caseStudies: CaseStudy[] = [
  {
    id: 1,
    service: 'AI Agents Development',
    title: 'Intelligent Customer Support Automation',
    client: 'TechCorp Solutions',
    industry: 'Technology/SaaS',
    challenge:
    'High volume of repetitive customer inquiries overwhelming support team, leading to delayed response times and decreased customer satisfaction.',
    solution:
    'Developed custom AI agents powered by Azure OpenAI to handle tier-1 support queries, integrated with existing CRM and knowledge base systems.',
    results: [
    'Reduced response time from 4 hours to under 2 minutes',
    'Automated 65% of customer inquiries',
    'Increased customer satisfaction score by 42%',
    'Freed support team to focus on complex issues'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7085398-1764660533982.png",
    alt: 'Modern office workspace with multiple computer monitors displaying customer support dashboard and analytics',
    metrics: [
    { label: 'Response Time', value: '2 min' },
    { label: 'Automation Rate', value: '65%' },
    { label: 'CSAT Increase', value: '+42%' }]

  },
  {
    id: 2,
    service: 'Data Engineering',
    title: 'Enterprise Data Lake Implementation',
    client: 'FinanceFirst Bank',
    industry: 'BFSI',
    challenge:
    'Siloed data across multiple legacy systems preventing real-time analytics and regulatory compliance reporting.',
    solution:
    'Built scalable data lake on Azure with automated ETL pipelines, real-time streaming, and comprehensive data governance framework.',
    results: [
    'Unified data from 12 legacy systems',
    'Enabled real-time fraud detection',
    'Reduced reporting time from days to hours',
    'Achieved 100% regulatory compliance'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d5e4a6aa-1764658027683.png",
    alt: 'Financial data visualization dashboard showing charts, graphs and real-time analytics on large display screens',
    metrics: [
    { label: 'Systems Unified', value: '12' },
    { label: 'Time Saved', value: '85%' },
    { label: 'Compliance', value: '100%' }]

  },
  {
    id: 3,
    service: 'Contract Staffing',
    title: 'Rapid Team Scaling for Product Launch',
    client: 'HealthTech Innovations',
    industry: 'Healthcare',
    challenge:
    'Urgent need for specialized developers to meet aggressive product launch deadline without long-term hiring commitments.',
    solution:
    'Provided team of 8 senior developers with healthcare domain expertise, integrated within 2 weeks with existing development processes.',
    results: [
    'Met product launch deadline ahead of schedule',
    'Zero onboarding delays with pre-vetted talent',
    'Seamless integration with existing team',
    'Flexible scaling as project needs evolved'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfbc27cd-1766500363073.png",
    alt: 'Diverse team of software developers collaborating around desk with laptops and code on screens in modern office',
    metrics: [
    { label: 'Team Size', value: '8' },
    { label: 'Onboarding', value: '2 weeks' },
    { label: 'Launch', value: 'On Time' }]

  }];


  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Success Stories
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Real results from real clients across industries
          </p>
        </div>

        <div className="space-y-12">
          {caseStudies.map((study, index) =>
          <div
            key={study.id}
            className={`bg-card rounded-lg shadow-lg overflow-hidden ${
            index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} md:flex`
            }>

              <div className="md:w-2/5 relative h-64 md:h-auto overflow-hidden">
                <AppImage
                src={study.image}
                alt={study.alt}
                className="w-full h-full object-cover" />

                <div className="absolute top-4 left-4">
                  <span className="px-4 py-2 bg-primary text-primary-foreground font-headline font-semibold text-sm rounded-full shadow-md">
                    {study.service}
                  </span>
                </div>
              </div>

              <div className="md:w-3/5 p-6 md:p-8">
                <div className="mb-4">
                  <h3 className="text-2xl md:text-3xl font-headline font-bold text-foreground mb-2">
                    {study.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Icon
                      name="BuildingOfficeIcon"
                      size={16}
                      variant="solid"
                      className="text-primary" />

                      <span>{study.client}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon
                      name="BriefcaseIcon"
                      size={16}
                      variant="solid"
                      className="text-secondary" />

                      <span>{study.industry}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="font-headline font-bold text-foreground mb-2 flex items-center space-x-2">
                      <Icon
                      name="ExclamationTriangleIcon"
                      size={18}
                      variant="solid"
                      className="text-warning" />

                      <span>Challenge</span>
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {study.challenge}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-headline font-bold text-foreground mb-2 flex items-center space-x-2">
                      <Icon
                      name="LightBulbIcon"
                      size={18}
                      variant="solid"
                      className="text-accent" />

                      <span>Solution</span>
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {study.solution}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
                    <Icon
                    name="ChartBarIcon"
                    size={18}
                    variant="solid"
                    className="text-success" />

                    <span>Key Results</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {study.results.map((result, idx) =>
                  <div key={idx} className="flex items-start space-x-2">
                        <Icon
                      name="CheckCircleIcon"
                      size={16}
                      variant="solid"
                      className="text-success flex-shrink-0 mt-0.5" />

                        <span className="text-sm text-foreground">{result}</span>
                      </div>
                  )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-4 border-t border-border">
                  {study.metrics.map((metric, idx) =>
                <div key={idx} className="text-center">
                      <p className="text-2xl font-headline font-bold text-primary">
                        {metric.value}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {metric.label}
                      </p>
                    </div>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default CaseStudyShowcase;